import { useQuery } from "@tanstack/react-query";
import { apiFetch, apiJson } from "@/api/client";
import { segment, withQuery } from "@/api/query";

/*
 * Mirrors api/src/pbo_invoicing/statements/. The statement is derived on every read from the
 * same invoices and payments the invoice pages show, so the two can never disagree. Money is
 * a string ("1400.00"); dates are YYYY-MM-DD.
 */

export type StatementKind = "invoice" | "credit_note" | "payment" | "write_off";

export type StatementLine = {
  on: string;
  kind: StatementKind;
  reference: string;
  description: string;
  debit: string;
  credit: string;
  balance: string;
  invoice_id: string | null;
  invoice_no: string | null;
};

export type AgeingSlice = { key: string; label: string; amount: string };

export type Statement = {
  client: {
    id: string;
    code: string;
    legal_name: string;
    address_lines: string[];
    payment_days: number;
  };
  currency: string;
  since: string | null;
  as_of: string;
  opening_balance: string;
  invoiced: string;
  received: string;
  written_off: string;
  closing_balance: string;
  lines: StatementLine[];
  ageing: AgeingSlice[];
  oldest_unpaid_days: number;
  awaiting_bank_letter: boolean;
  /** Accrued under the late-payment clause on the open invoices; not on the PDF. */
  late_charge_accrued: string;
};

export type StatementRange = { from?: string; as_of?: string; currency?: string };

export const statementKeys = {
  all: ["statements"] as const,
  one: (clientId: string, range: StatementRange) =>
    [...statementKeys.all, clientId, range] as const,
};

function path(clientId: string, range: StatementRange, suffix = ""): string {
  return withQuery(`/clients/${segment(clientId)}/statement${suffix}`, {
    from: range.from,
    as_of: range.as_of,
    currency: range.currency,
  });
}

export function useStatement(clientId: string, range: StatementRange = {}) {
  return useQuery({
    queryKey: statementKeys.one(clientId, range),
    queryFn: () => apiJson<Statement>(path(clientId, range)),
    enabled: clientId !== "",
  });
}

export type PdfFile = { blob: Blob; filename: string };

export async function fetchStatementPdf(
  clientId: string,
  range: StatementRange = {},
): Promise<PdfFile> {
  const res = await apiFetch(path(clientId, range, ".pdf"));
  const match = /filename="([^"]+)"/.exec(res.headers.get("Content-Disposition") ?? "");
  return { blob: await res.blob(), filename: match?.[1] ?? "statement.pdf" };
}
